import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import api, { formatINR } from "@/lib/api";
import { Spinner } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Warning } from "@phosphor-icons/react";

const STATUSES = ["OPEN", "UNDER_REVIEW", "WAITING_FOR_CUSTOMER", "WAITING_FOR_OWNER", "RESOLVED", "REJECTED"];
const COLORS = { OPEN: "bg-red-100 text-red-700", UNDER_REVIEW: "bg-amber-100 text-amber-700", WAITING_FOR_CUSTOMER: "bg-blue-100 text-blue-700", WAITING_FOR_OWNER: "bg-blue-100 text-blue-700", RESOLVED: "bg-green-100 text-green-700", REJECTED: "bg-stone-200 text-stone-500" };

export default function AdminDisputes() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const load = () => api.get("/admin/disputes", { params: filter ? { status: filter } : {} }).then((r) => setData(r.data)).catch(() => toast.error("Couldn't load disputes"));
  useEffect(() => { load(); }, [filter]);

  const setStatus = (d, status) => api.patch(`/admin/disputes/${d.id}`, { status }).then(() => { toast.success("Updated"); load(); }).catch(() => toast.error("Failed"));

  if (!data) return <DashboardLayout nav={adminNav} title="Admin"><div className="flex justify-center py-24"><Spinner /></div></DashboardLayout>;
  return (
    <DashboardLayout nav={adminNav} title="Admin">
      <div className="flex items-center gap-2"><Warning size={26} className="text-primary" /><h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Disputes</h1></div>
      <p className="mt-1 text-sm text-stone-500">No money is auto-refunded or withheld on a dispute — admin action is required.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {["", ...STATUSES].map((s) => <button key={s} data-testid={`dispute-filter-${s || "all"}`} onClick={() => setFilter(s)} className={`rounded-full px-3 py-1.5 text-xs font-medium ${filter === s ? "bg-primary text-white" : "border border-stone-200 text-stone-600 hover:bg-accent"}`}>{s || "All"}</button>)}
      </div>
      <div className="mt-4 space-y-3" data-testid="disputes-list">
        {data.disputes.length === 0 ? <p className="rounded-xl border border-dashed border-stone-200 p-6 text-center text-sm text-stone-400">No disputes.</p> :
          data.disputes.map((d) => (
            <div key={d.id} className="rounded-2xl border border-stone-200 bg-white p-4" data-testid={`dispute-${d.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-stone-800">{d.dispute_ref}</span>
                <span className="text-xs text-stone-400">{d.booking_ref} · raised by {d.raised_role} {d.raised_name}</span>
                <span className={`ml-auto rounded-full px-2 py-0.5 text-xs font-medium ${COLORS[d.status] || "bg-stone-100"}`}>{d.status}</span>
              </div>
              <p className="mt-2 text-sm font-medium text-stone-700">{d.reason}</p>
              <p className="text-sm text-stone-500">{d.description}</p>
              {d.financial && <p className="mt-2 text-xs text-stone-400">Booking {formatINR(d.financial.booking_value)} · Paid {formatINR(d.financial.payment)} · Refund {formatINR(d.financial.refund)} · Commission {formatINR(d.financial.commission)} · Payout {formatINR(d.financial.payout_net)}</p>}
              <div className="mt-3 flex flex-wrap gap-1">
                {STATUSES.map((s) => <button key={s} data-testid={`dispute-${d.id}-set-${s}`} onClick={() => setStatus(d, s)} className={`rounded-md border px-2 py-1 text-[11px] ${d.status === s ? "border-primary text-primary" : "border-stone-300 text-stone-600"}`}>{s.replace(/_/g, " ")}</button>)}
              </div>
            </div>
          ))}
      </div>
    </DashboardLayout>
  );
}
